import { useState, useMemo, useCallback, useEffect } from 'react';

const ZOOM_IN_FACTOR = 0.75;
const ZOOM_OUT_FACTOR = 1.33;
export const CHART_ZOOM_RESET_ALL = 'chartZoomResetAll';

export interface UseChartZoomOptions {
  data: any[];
  xDataKey: string;
  yDataKeys?: string[];
  xNumeric?: boolean;
}

function getExtent(values: number[]): [number, number] {
  if (values.length === 0) return [0, 1];
  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return [min - 1, max + 1];
  return [min, max];
}

function getNumericExtent(data: any[], key: string): [number, number] {
  const values = data
    .map((d) => {
      const v = d[key];
      const n = typeof v === 'number' ? v : parseFloat(String(v));
      return Number.isFinite(n) ? n : NaN;
    })
    .filter((n) => !Number.isNaN(n));
  return getExtent(values);
}

export function useChartZoom(options: UseChartZoomOptions) {
  const { data, xDataKey, yDataKeys = [], xNumeric = true } = options;

  const fullXDomain = useMemo((): [number, number] | undefined => {
    if (!data || data.length === 0) return undefined;
    if (xNumeric) return getNumericExtent(data, xDataKey);
    return [0, Math.max(0, data.length - 1)];
  }, [data, xDataKey, xNumeric]);

  const fullYDomain = useMemo((): [number, number] | undefined => {
    if (!data || data.length === 0 || yDataKeys.length === 0) return undefined;
    const allValues: number[] = [];
    data.forEach((d) => {
      yDataKeys.forEach((key) => {
        const v = d[key];
        const n = typeof v === 'number' ? v : parseFloat(String(v));
        if (Number.isFinite(n)) allValues.push(n);
      });
    });
    if (allValues.length === 0) return undefined;
    return getExtent(allValues);
  }, [data, yDataKeys]);

  const [xDomain, setXDomain] = useState<[number, number] | undefined>(undefined);
  const [yDomain, setYDomain] = useState<[number, number] | undefined>(undefined);

  useEffect(() => {
    if (fullXDomain) setXDomain((prev) => (prev == null ? fullXDomain : prev));
  }, [fullXDomain]);

  useEffect(() => {
    if (fullYDomain) setYDomain((prev) => (prev == null ? fullYDomain : prev));
  }, [fullYDomain]);

  const reset = useCallback(() => {
    setXDomain(fullXDomain ?? undefined);
    setYDomain(fullYDomain ?? undefined);
  }, [fullXDomain, fullYDomain]);

  useEffect(() => {
    const onResetAll = () => reset();
    window.addEventListener(CHART_ZOOM_RESET_ALL, onResetAll);
    return () => window.removeEventListener(CHART_ZOOM_RESET_ALL, onResetAll);
  }, [reset]);

  const currentX = xDomain ?? fullXDomain;
  const currentY = yDomain ?? fullYDomain;

  const zoomIn = useCallback(() => {
    if (!fullXDomain || !currentX) return;
    const [min, max] = currentX;
    const center = (min + max) / 2;
    const range = (max - min) * ZOOM_IN_FACTOR;
    const newMin = Math.max(fullXDomain[0], center - range / 2);
    const newMax = Math.min(fullXDomain[1], center + range / 2);
    setXDomain([newMin, newMax]);
  }, [fullXDomain, currentX]);

  const zoomOut = useCallback(() => {
    if (!fullXDomain || !currentX) return;
    const [min, max] = currentX;
    const center = (min + max) / 2;
    const range = Math.min((max - min) * ZOOM_OUT_FACTOR, fullXDomain[1] - fullXDomain[0]);
    let newMin = center - range / 2;
    let newMax = center + range / 2;
    if (newMin < fullXDomain[0]) {
      newMin = fullXDomain[0];
      newMax = Math.min(fullXDomain[1], newMin + range);
    }
    if (newMax > fullXDomain[1]) {
      newMax = fullXDomain[1];
      newMin = Math.max(fullXDomain[0], newMax - range);
    }
    setXDomain([newMin, newMax]);
  }, [fullXDomain, currentX]);

  const setXDomainFromBrush = useCallback(
    (startIndex: number, endIndex: number) => {
      if (!data || data.length === 0) return;
      const start = Math.max(0, Math.min(startIndex, data.length - 1));
      const end = Math.max(0, Math.min(endIndex, data.length - 1));
      const i0 = Math.min(start, end);
      const i1 = Math.max(start, end);
      if (xNumeric) {
        const v0 = Number(data[i0]?.[xDataKey]) ?? (fullXDomain ? fullXDomain[0] : 0);
        const v1 = Number(data[i1]?.[xDataKey]) ?? (fullXDomain ? fullXDomain[1] : 1);
        setXDomain([v0, v1]);
      } else {
        setXDomain([i0, i1]);
      }
    },
    [data, xDataKey, xNumeric, fullXDomain]
  );

  const isZoomed =
    currentX != null &&
    fullXDomain != null &&
    (currentX[0] !== fullXDomain[0] || currentX[1] !== fullXDomain[1]);

  const brushIndices = useMemo(() => {
    if (!data || data.length === 0 || !currentX || !fullXDomain)
      return { startIndex: 0, endIndex: data ? data.length - 1 : 0 };
    if (!xNumeric) {
      const start = Math.max(0, Math.floor(currentX[0]));
      const end = Math.min(data.length - 1, Math.ceil(currentX[1]));
      return { startIndex: start, endIndex: end };
    }
    let startIndex = 0;
    let endIndex = data.length - 1;
    for (let i = 0; i < data.length; i++) {
      const v = Number(data[i]?.[xDataKey]);
      if (Number.isFinite(v) && v >= currentX[0]) {
        startIndex = i;
        break;
      }
    }
    for (let i = data.length - 1; i >= 0; i--) {
      const v = Number(data[i]?.[xDataKey]);
      if (Number.isFinite(v) && v <= currentX[1]) {
        endIndex = i;
        break;
      }
    }
    return { startIndex, endIndex };
  }, [data, xDataKey, currentX, fullXDomain, xNumeric]);

  return {
    xDomain: currentX,
    yDomain: currentY,
    fullXDomain,
    fullYDomain,
    zoomIn,
    zoomOut,
    reset,
    setXDomainFromBrush,
    isZoomed,
    brushStartIndex: brushIndices.startIndex,
    brushEndIndex: brushIndices.endIndex
  };
}

export function dispatchChartZoomResetAll() {
  window.dispatchEvent(new CustomEvent(CHART_ZOOM_RESET_ALL));
}
